import { api } from './client'
import {
  IconDashboard, IconApps, IconBox, IconChecklist, IconDatabase, IconBell,
  IconClipboardList, IconPigMoney, IconShieldCheck, IconPlug, IconMessageCircle,
  IconBrandGitlab, IconActivity, IconUsersGroup, IconSitemap, IconKey, IconNetwork,
  type TablerIcon,
} from '@tabler/icons-react'

// `critical` existe no banco desde a MIGRATION_307 (`terraform:criar_ambiente`) e
// a 327 (`terraform:aprovar_producao`). Sem ele aqui, o selo caia no fallback
// "Leitura" — o mesmo engano do systemctl no host (CLAUDE.md, RBAC).
export type PermissionSensitivity = 'read' | 'write' | 'sensitive' | 'critical'

export interface Permission {
  id: number
  resource: string
  action: string
  label: string
  description: string
  sensitivity: PermissionSensitivity
}

/** Metadado de apresentação de um resource (de `permission_resources`).
 *  Dirige o agrupamento seção→item do menu na tela de Permissões. */
export interface PermissionResourceMeta {
  resource: string
  label: string
  description: string
  menu_section: string
  menu_item: string
  icon_key: string
  sort_order: number
}

/** Catálogo completo retornado por GET /v1/permissions. */
export interface PermissionsCatalog {
  items: Permission[]
  resources: PermissionResourceMeta[]
}

/** Mapa icon_key (seed `permission_resources`) → ícone Tabler. */
export const ICON_KEY_MAP: Record<string, TablerIcon> = {
  'dashboard':       IconDashboard,
  'apps':            IconApps,
  'box':             IconBox,
  'checklist':       IconChecklist,
  'database':        IconDatabase,
  'bell':            IconBell,
  'clipboard-list':  IconClipboardList,
  'pig-money':       IconPigMoney,
  'shield-check':    IconShieldCheck,
  'plug':            IconPlug,
  'message-circle':  IconMessageCircle,
  'brand-gitlab':    IconBrandGitlab,
  'activity':        IconActivity,
  'users-group':     IconUsersGroup,
  'sitemap':         IconSitemap,
  // `network` ja era usado pelas linhas de VPN em permission_resources e nao
  // estava no mapa: caia no IconKey generico. A MIGRATION_220 tambem o usa
  // para `ipam` e `vpn_diag`.
  'network':         IconNetwork,
}

/** Resolve um icon_key para o componente Tabler, com fallback IconKey. */
export function iconForKey(key: string | undefined | null): TablerIcon {
  return (key && ICON_KEY_MAP[key]) || IconKey
}

/** Restrição (cap) por ambiente ou tenant: LIMITA as ações que o perfil
 *  concede naquele escopo a um subconjunto da base. Validado server-side
 *  (permission_ids ⊆ base). Sem cap = base completa. */
export interface TemplateCap {
  scope_kind: 'environment' | 'tenant'
  scope_value: string
  permission_ids: number[]
}

/** Resumo por template do reconcile de `user_tenants` (`rbac.py:738`). */
export interface ReconcilePreview {
  users: number
  added: number
  removed: number
  changes: { user_id: number; add: number[]; remove: number[] }[]
}

export interface PermissionTemplate {
  id: number
  name: string
  description: string
  /** Quando true, template cobre dinamicamente todos os tenants ativos
   * (atuais e futuros) — MIGRATION_098. Sobrepoe `tenants`. */
  all_tenants: boolean
  /** Quando true, template e elegivel como destino padrao de roteamento
   * de chamados (encaminhamento entre times). */
  is_default_routing_target?: boolean
  /** Escopo por OU (org-driven). Lista de ou_ids da AWS Organizations.
   * Quando preenchido, os tenants sao derivados dinamicamente do subtree
   * dessas OUs. Sobrepoe `tenants` (lista explicita legada). Pode nao vir
   * em respostas antigas do backend — tratar como [] quando ausente. */
  ou_ids?: string[]
  permissions: Permission[]
  tenants: { id: number; name: string; environment: string }[]
  /** Restrições (caps) por ambiente/tenant — RBAC_TEMPLATE_CAPS_ENABLED.
   *  Pode não vir em respostas com a flag OFF — tratar como [] quando ausente. */
  caps?: TemplateCap[]
  linked_users: { id: number; email: string; full_name: string }[]
  linked_users_count: number
  /** Classificacao do escopo, resolvida no backend (inclui `ou_ids`).
   *  `none` = o perfil nao alcanca nenhum tenant. Pode nao vir em respostas
   *  antigas — tratar `undefined` como DESCONHECIDO, nunca como `none`. */
  scope_kind?: 'all_tenants' | 'ou' | 'explicit' | 'none'
  /** Quantidade de tenants efetivamente alcancados. */
  effective_tenant_count?: number
  /** Tenants resolvidos. Vazia quando `scope_kind === 'all_tenants'`: o backend
   *  nao enumera nesse caso, so conta. */
  effective_tenants?: { id: number; name: string; environment: string }[]
}

/**
 * True quando o perfil NÃO concede nenhum tenant efetivo: sem `all_tenants`,
 * sem `ou_ids` (escopo por OU) e sem `tenants` explícitos. Caso da armadilha
 * "duplicar → aplicar" (UX C3): o clone nasce sem escopo e, aplicado, deixa o
 * usuário sem acesso efetivo. Usado para avisar antes de aplicar.
 */
export function templateGrantsNoTenants(tmpl: PermissionTemplate): boolean {
  // `scope_kind` vem do backend com `ou_ids` JÁ RESOLVIDO e manda quando existe.
  // A heurística abaixo dizia "concede" só por haver `ou_ids`, mesmo quando a OU
  // estava vazia — e o card, lendo o mesmo estado por outro caminho, dizia
  // "escopo global". Eram duas leituras opostas do mesmo perfil.
  if (tmpl.scope_kind) return tmpl.scope_kind === 'none'
  // Backend anterior a 2026-08: cai na heurística antiga.
  if (tmpl.all_tenants) return false
  if ((tmpl.ou_ids?.length ?? 0) > 0) return false
  if ((tmpl.tenants?.length ?? 0) > 0) return false
  return true
}

/**
 * Rotulo de escopo do card do perfil, a partir da classificacao do backend.
 *
 * Substitui a heuristica de `Permissoes.tsx`, que derivava o escopo de
 * `tenants.length` — um campo que nunca incluia os tenants resolvidos por
 * `ou_ids`. O mesmo estado vazio era exibido como "Escopo global (todos os
 * tenants)" no card e como "sem acesso efetivo" no alerta de aplicacao.
 *
 * `scope_kind` ausente (backend anterior a 2026-08) NAO e tratado como `none`:
 * o rotulo diz que a informacao nao veio, e o tom e de atencao. Inventar
 * "escopo global" nesse caso e exatamente o bug que estamos removendo.
 */
export function templateScopeSummary(
  t: PermissionTemplate,
): { label: string; tone: 'ok' | 'warn' } {
  const n = t.effective_tenant_count
  const plural = (v: number) => (v === 1 ? 'tenant' : 'tenants')
  switch (t.scope_kind) {
    case 'all_tenants':
      return { label: `Todos os tenants${n != null ? ` (${n})` : ''}`, tone: 'ok' }
    case 'ou':
      return { label: `${n ?? 0} ${plural(n ?? 0)} (por OU)`, tone: 'ok' }
    case 'explicit':
      return { label: `${n ?? 0} ${plural(n ?? 0)} (lista explícita)`, tone: 'ok' }
    case 'none': {
      // `scope_kind` descreve só o escopo PRÓPRIO do template (all_tenants /
      // OU / lista explícita). Um perfil sem escopo próprio ainda alcança
      // tenants quando é aplicado POR ORGANIZAÇÃO
      // (`organization_users.template_id`): aí a decisão sai daquele template e
      // `has_permission` nunca consulta a lista própria dele
      // (rbac.py:1580-1615). Dizer "não concede acesso" nesse caso é falso — era
      // o que o card dizia do DEV-PLENO, com 23 atribuições ativas.
      // `tenants` é a união (legado ∪ tenants das orgs) que o backend já serve.
      const viaOrg = t.tenants?.length ?? 0
      if (viaOrg > 0) {
        return {
          label: `Escopo próprio: nenhum · por organização: ${viaOrg} ${plural(viaOrg)}`,
          tone: 'ok',
        }
      }
      return { label: 'Nenhum tenant — não concede acesso', tone: 'warn' }
    }
    default:
      return { label: 'Escopo não informado', tone: 'warn' }
  }
}

export interface ListTemplatesOptions {
  /** Quando true, retorna apenas templates com permissoes de atendimento
   * de chamados (admin:full OU chamados:{close,assign,manage,comment}). */
  routable_only?: boolean
}

export interface UserPermissions {
  global_permissions: string[]
  tenant_permissions: Record<string, string[]>
  applied_template_id: number | null
  applied_template_name: string | null
  /** `true` = superusuário (flag `is_admin` ou função `admin`). Nesse caso as
   *  listas vêm vazias de propósito: o backend não enumera ~128 permissões × N
   *  tenants. Sem este campo a tela dizia "0 permissões ativas" para quem tem
   *  acesso a tudo. */
  is_superuser?: boolean
  /** `plataforma:global_view`: LEITURA em qualquer tenant, inclusive nos que não
   *  aparecem nas abas. Mutação continua seguindo o escopo do perfil. */
  global_read_all?: boolean
  /** Ações que a base do perfil concede mas o cap do tenant REMOVEU.
   *  tenant_id (string) → códigos `resource:action`. */
  capped_tenants?: Record<string, string[]>
}

/** Tenant herdado do template (escopo efetivo do perfil aplicado). */
export interface InheritedTenant {
  id: number
  name: string
  environment: string
}

/** Organização de negócio atribuída ao usuário (id + nome). */
export interface AssignedOrganization {
  id: number
  name: string
  logo_url?: string | null
}

/** Organização atribuída ao usuário com o perfil POR ORG
 *  (per-org-template §4). `template_id`/`template_name` = null quando a
 *  associação não tem override per-org (cai no fallback global). */
export interface AssignedOrganizationDetail {
  id: number
  name: string
  role: string
  template_id: number | null
  template_name: string | null
}

/** Escopo de Organizações (de negócio) de um usuário.
 *  - `assigned`: Organizações de negócio atribuídas diretamente ao usuário
 *    (de `organization_users`), editáveis.
 *  - `inherited_tenants`: tenants EFETIVOS do template aplicado (cobre lista
 *    explícita legado, escopo por OU e all_tenants) — concedidos pelo perfil
 *    e exibidos read-only. */
export interface UserOrganizations {
  user_id: number
  assigned: AssignedOrganizationDetail[]
  inherited_tenants: InheritedTenant[]
  /** Catálogo de templates disponíveis para o usuário — união dos catálogos
   *  das organizações às quais ele pertence (MIGRATION_134). Quando
   *  `unrestricted === true` esta lista não restringe (mostrar todos). */
  available_templates?: { id: number; name: string }[]
  /** `true` quando a união dos catálogos é vazia (sem org com catálogo ou
   *  usuário sem org) → sem restrição de perfil (fail-safe). */
  unrestricted?: boolean
}

export interface ReconciledTenant {
  id: number
  name: string
  environment: string
}

/** Resultado do sync-total das organizações de um usuário. */
export interface SetUserOrganizationsResult {
  user_id: number
  assigned: number[]
  reconcile: {
    added: number
    removed: number
    // UX-M4: nomes dos tenants reconciliados (opcional — backend pós-2026-06-05).
    added_tenants?: ReconciledTenant[]
    removed_tenants?: ReconciledTenant[]
  }
}

/** Visão reversa de uma Organização (OU): templates associados.
 *  Escopo: associação direta (`permission_template_ous`) + templates
 *  `all_tenants` (cobrem todas as OUs). Herança por subtree fica fora.
 *  O campo `users` foi aposentado junto com a tabela `user_ou_scope`. */
export interface OrganizationTemplates {
  ou_id: string
  templates: {
    id: number
    name: string
    /** `ou` = associado diretamente; `all_tenants` = cobre todas as OUs. */
    scope: 'ou' | 'all_tenants'
  }[]
}

export const permissionsApi = {
  listPermissions: async (): Promise<PermissionsCatalog> => {
    const res = await api.get<{ items: Permission[]; resources?: PermissionResourceMeta[] }>('/v1/permissions')
    return { items: res.data.items, resources: res.data.resources ?? [] }
  },

  listTemplates: async (opts: ListTemplatesOptions = {}): Promise<PermissionTemplate[]> => {
    const params = new URLSearchParams()
    if (opts.routable_only) params.set('routable_only', 'true')
    const qs = params.toString() ? `?${params.toString()}` : ''
    const res = await api.get<{ items: PermissionTemplate[] }>(`/v1/permission-templates${qs}`)
    return res.data.items
  },

  createTemplate: async (
    name: string,
    description: string,
    permission_ids: number[],
    tenant_ids: number[],
    all_tenants: boolean = false,
    ou_ids: string[] = [],
    caps?: TemplateCap[],
  ) => {
    // `caps` ausente (undefined) → omitido do body → backend não toca nas caps
    // existentes (semântica "ausente = sem mudança"; evita wipe com a flag OFF).
    const body: Record<string, unknown> = { name, description, permission_ids, tenant_ids, all_tenants, ou_ids }
    if (caps !== undefined) body.caps = caps
    const res = await api.post<{ id: number }>('/v1/permission-templates', body)
    return res.data
  },

  updateTemplate: async (
    id: number,
    name: string,
    description: string,
    permission_ids: number[],
    tenant_ids: number[],
    all_tenants: boolean = false,
    ou_ids: string[] = [],
    caps?: TemplateCap[],
  ) => {
    // `caps` ausente (undefined) → omitido do body → backend preserva as caps
    // existentes (semântica "ausente = sem mudança"; evita wipe com a flag OFF).
    const body: Record<string, unknown> = { name, description, permission_ids, tenant_ids, all_tenants, ou_ids }
    if (caps !== undefined) body.caps = caps
    await api.put(`/v1/permission-templates/${id}`, body)
  },

  deleteTemplate: async (id: number) => {
    await api.delete(`/v1/permission-templates/${id}`)
  },

  /** Preview (dry-run) do reconcile de escopo que um save de perfil dispara.
   *
   *  `PUT /permission-templates/{id}` chama `reconcile_user_tenants_for_template`
   *  SEM condição (`api.py:8686`): sync TOTAL do `user_tenants` de quem tem o
   *  perfil como global — inclusive num save que só mexeu numa restrição. Este
   *  endpoint já existe com `dry_run` default `true` e não grava nada, então dá
   *  para perguntar antes. Devolve o resumo do template ou `null`. */
  reconcileTenantScopeDryRun: async (
    template_id: number,
  ): Promise<ReconcilePreview | null> => {
    const res = await api.post<{ reconcile: ReconcilePreview[] }>(
      '/v1/rbac/reconcile-tenant-scope',
      { template_id, dry_run: true, autoconvert: false },
    )
    return res.data.reconcile?.[0] ?? null
  },

  /** Duplica um template copiando SÓ as permissões (escopo de tenants/OU em
   *  branco; flag de roteamento padrão nunca é copiada). Retorna o novo id. */
  duplicateTemplate: async (id: number, name: string) => {
    const res = await api.post<{ id: number }>(`/v1/permission-templates/${id}/duplicate`, { name })
    return res.data
  },

  getUserPermissions: async (userId: number): Promise<UserPermissions> => {
    const res = await api.get<UserPermissions>(`/v1/users/${userId}/permissions`)
    return res.data
  },

  setUserPermissions: async (userId: number, tenant_id: number | null, permission_ids: number[]) => {
    await api.put(`/v1/users/${userId}/permissions`, { tenant_id, permission_ids })
  },

  applyTemplate: async (userId: number, template_id: number) => {
    await api.post(`/v1/users/${userId}/apply-template`, { template_id })
  },

  applyTemplateWithScope: async (userId: number, template_id: number, tenant_ids: number[] | null) => {
    await api.post(`/v1/users/${userId}/apply-template`, { template_id, tenant_ids })
  },

  unlinkTemplate: async (userId: number) => {
    await api.delete(`/v1/users/${userId}/applied-template`)
  },

  applyTemplateBatch: async (templateId: number, userIds: number[]) => {
    const res = await api.post<{ count: number }>(`/v1/permission-templates/${templateId}/apply-batch`, { user_ids: userIds })
    return res.data
  },

  getUserOrganizations: async (userId: number): Promise<UserOrganizations> => {
    const res = await api.get<UserOrganizations>(`/v1/users/${userId}/organizations`)
    return res.data
  },

  setUserOrganizations: async (
    userId: number,
    organization_ids: number[],
    template_id?: number | null,
  ): Promise<SetUserOrganizationsResult> => {
    const body: { organization_ids: number[]; template_id?: number } = { organization_ids }
    if (template_id != null) body.template_id = template_id
    const res = await api.put<SetUserOrganizationsResult>(
      `/v1/users/${userId}/organizations`,
      body,
    )
    return res.data
  },

  /** Lista enxuta de organizações de negócio (id+name) para dropdowns. */
  listOrganizationsLite: async (): Promise<AssignedOrganization[]> => {
    const res = await api.get<{ items: AssignedOrganization[] }>('/v1/organizations/lite')
    return res.data.items
  },

  /** Visão reversa (read-only): templates e usuários associados a uma OU.
   *  Backend: GET /v1/organizations/{ou_id}/permission-templates. */
  getOrganizationTemplates: async (ouId: string): Promise<OrganizationTemplates> => {
    const res = await api.get<OrganizationTemplates>(
      `/v1/organizations/${encodeURIComponent(ouId)}/permission-templates`,
    )
    return res.data
  },
}

// Agrupa permissions por resource para exibição em grid
export function groupByResource(permissions: Permission[]): Record<string, Permission[]> {
  const groups: Record<string, Permission[]> = {}
  for (const p of permissions) {
    if (!groups[p.resource]) groups[p.resource] = []
    groups[p.resource].push(p)
  }
  return groups
}

